import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Filter, LogIn, LogOut, Monitor, Terminal, MonitorPlay, Shield, User, ChevronLeft, ChevronRight } from 'lucide-react'
import { getActivityLog, type AuditLogEvent } from '../../lib/api'
import { useAutoRefresh } from '../../hooks/useAutoRefresh'

// Action type configuration
const ACTION_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  login: { icon: <LogIn className="w-3 h-3" />, color: 'text-green-500', label: 'Login' },
  logout: { icon: <LogOut className="w-3 h-3" />, color: 'text-gray-400', label: 'Logout' },
  workspace_code: { icon: <Monitor className="w-3 h-3" />, color: 'text-blue-500', label: 'Code Editor' },
  workspace_terminal: { icon: <Terminal className="w-3 h-3" />, color: 'text-purple-500', label: 'Terminal' },
  workspace_desktop: { icon: <MonitorPlay className="w-3 h-3" />, color: 'text-amber-500', label: 'Desktop' },
  admin_provision: { icon: <Shield className="w-3 h-3" />, color: 'text-cyan-500', label: 'Provision' },
  admin_impersonate: { icon: <User className="w-3 h-3" />, color: 'text-orange-500', label: 'Impersonate' },
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export default function Activity() {
  const navigate = useNavigate()
  const [events, setEvents] = useState<AuditLogEvent[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionFilter, setActionFilter] = useState<string>('')
  const [page, setPage] = useState(0)
  const limit = 50

  const loadActivity = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getActivityLog({
        action: actionFilter || undefined,
        limit,
        offset: page * limit,
      })
      setEvents(data.events)
      setTotal(data.total)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity')
      throw err  // Allow useAutoRefresh to detect the error for backoff
    } finally {
      setLoading(false)
    }
  }, [actionFilter, page])

  useEffect(() => {
    loadActivity()
  }, [loadActivity])

  // Auto-refresh with exponential backoff on errors
  useAutoRefresh(loadActivity, {
    baseInterval: 30000,
    maxInterval: 300000,  // 5 minutes max backoff
  })

  const totalPages = Math.ceil(total / limit)

  function getActionDisplay(action: string) {
    return ACTION_CONFIG[action] || {
      icon: <User className="w-3 h-3" />,
      color: 'text-muted-foreground',
      label: action.replace(/_/g, ' '),
    }
  }

  const actionTypes = [
    { value: '', label: 'All Actions' },
    { value: 'login', label: 'Logins' },
    { value: 'logout', label: 'Logouts' },
    { value: 'workspace_code', label: 'Code Editor' },
    { value: 'workspace_terminal', label: 'Terminal' },
    { value: 'workspace_desktop', label: 'Desktop' },
    { value: 'admin_provision', label: 'Admin: Provision' },
    { value: 'admin_impersonate', label: 'Admin: Impersonate' },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Activity Log</h1>
          <p className="text-muted-foreground">
            System-wide user activity and audit trail
          </p>
        </div>
        <button
          onClick={loadActivity}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value)
              setPage(0) // Reset to first page on filter change
            }}
            className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {actionTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <div className="text-sm text-muted-foreground">
          Showing {events.length} of {total} events
        </div>
      </div>

      {/* Activity List */}
      {loading && events.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No activity recorded yet
        </div>
      ) : (
        <div className="space-y-2">
          {events.map((event) => {
            const actionDisplay = getActionDisplay(event.action)
            return (
              <div
                key={event.id}
                className="p-4 rounded-lg border border-border bg-card hover:bg-secondary/30 transition-colors cursor-pointer"
                onClick={() => navigate(`/admin/users/${event.user_id}`)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    {/* Action icon */}
                    <div className={`mt-1 ${actionDisplay.color}`}>
                      {actionDisplay.icon}
                    </div>

                    {/* Content */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">
                          {event.user_name || event.email.split('@')[0]}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${actionDisplay.color} bg-current/10`}>
                          {actionDisplay.label}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {event.email}
                        {event.resource_id && (
                          <span className="ml-2">
                            → <code className="text-xs">{event.resource_id}</code>
                          </span>
                        )}
                      </div>
                      {event.ip_address && (
                        <div className="text-xs text-muted-foreground mt-1">
                          IP: {event.ip_address}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timestamp */}
                  <div className="text-sm text-muted-foreground text-right">
                    <div>{formatRelativeTime(event.created_at)}</div>
                    <div className="text-xs">
                      {new Date(event.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
